import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import { bulkDeleteQuestions } from "../../../Redux/actions/questionAction";
import { errorToastMessage } from "../../../utils/toast";
import http from "../../../utils/http";

type Props = {
  open: boolean;
  onClose: () => void;
  questionIds: string[];
};

type DepQuestion = {
  id: string;
  label?: string;
  varname?: string;
};

const BulkRemoveConfirmModal: React.FC<Props> = ({
  open,
  onClose,
  questionIds,
}) => {
  const dispatch = useAppDispatch();
  const formId = useAppSelector((s) => s.question.formId);
  const studyId = useAppSelector((s) => s.question.studyId);
  const formLevel = useAppSelector((s) => s.question.formLevel);
  const formSubmitting = useAppSelector((s) => s.question.formSubmitting);

  const [loading, setLoading] = useState(true);
  const [calcBlocking, setCalcBlocking] = useState<DepQuestion[]>([]);
  const [displayWarnings, setDisplayWarnings] = useState<DepQuestion[]>([]);

  useEffect(() => {
    if (!open || questionIds.length === 0) return;

    const fetchDeps = async () => {
      setLoading(true);
      const prefix =
        formLevel === "platform" ? "/admin/registry/" : `/study/${studyId}/`;
      const selectedSet = new Set(questionIds);

      try {
        const results = await Promise.all(
          questionIds.map((id) =>
            http
              .get(`${prefix}forms/${formId}/question/${id}/dependencies`)
              .then((res) => ({ id, deps: res.data?.data ?? [] }))
          )
        );

        const blocking: DepQuestion[] = [];
        const warnings: DepQuestion[] = [];

        for (const { deps } of results) {
          for (const dep of deps) {
            if (selectedSet.has(dep.id)) continue;
            if (dep.type === "calculated_field" || dep.calculationTemplate) {
              if (!blocking.find((d) => d.id === dep.id)) blocking.push(dep);
            } else {
              if (!warnings.find((d) => d.id === dep.id)) warnings.push(dep);
            }
          }
        }

        setCalcBlocking(blocking);
        setDisplayWarnings(warnings);
      } catch (err) {
        errorToastMessage(err as Error);
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchDeps();
  }, [open, questionIds, formId, studyId, formLevel, onClose]);

  const handleConfirm = () => {
    dispatch(bulkDeleteQuestions(questionIds));
    onClose();
  };

  const canConfirm = !loading && calcBlocking.length === 0 && !formSubmitting;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Remove {questionIds.length} Question{questionIds.length !== 1 ? "s" : ""}
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {calcBlocking.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} mb={0.5}>
                  Cannot remove — the following questions are used in
                  calculated fields outside your selection:
                </Typography>
                {calcBlocking.map((d) => (
                  <Typography key={d.id} variant="body2">
                    • {d.varname || d.label || d.id}
                  </Typography>
                ))}
              </Alert>
            )}

            {displayWarnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} mb={0.5}>
                  The following questions have branching logic or dependencies
                  that reference your selection and will be removed:
                </Typography>
                {displayWarnings.map((d) => (
                  <Typography key={d.id} variant="body2">
                    • {d.varname || d.label || d.id}
                  </Typography>
                ))}
              </Alert>
            )}

            {calcBlocking.length === 0 && displayWarnings.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                This will permanently remove the {questionIds.length} selected
                question{questionIds.length !== 1 ? "s" : ""}. This action
                cannot be undone.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={formSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={!canConfirm}
        >
          Remove Selected
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkRemoveConfirmModal;
